import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { Download, CalendarDays, TrendingUp, Users, Scissors } from "lucide-react";
import { getReports } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const COLORS = ['#d4af37', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

export default function ReportsTab() {
  const { user } = useAuth();
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(1); // Start of month
    return d.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);

  const loadReports = useCallback(async () => {
    if (!user?.businessId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getReports(user.businessId, startDate, endDate);
      setReportData(data);
    } catch (err) {
      console.error("Raporlar yüklenemedi:", err);
      setError(err.message || "Raporlar alınırken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, [user, startDate, endDate]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const exportToCSV = () => {
    if (!reportData) return;

    let csvContent = "data:text/csv;charset=utf-8,\uFEFF"; // UTF-8 BOM

    csvContent += "=== GENEL OZET ===\n";
    csvContent += `Tarih Araligi;${startDate} - ${endDate}\n`;
    csvContent += `Toplam Randevu;${reportData.totalAppointments}\n`;
    csvContent += `Toplam Ciro;${reportData.totalRevenue} TL\n\n`;

    csvContent += "=== PERSONEL PERFORMANSI ===\n";
    csvContent += "Personel Adi;Randevu Sayisi;Ciro (TL)\n";
    reportData.staffStats.forEach(s => {
      csvContent += `"${s.staffName}";${s.count};${s.revenue}\n`;
    });
    csvContent += "\n";

    csvContent += "=== HIZMET PERFORMANSI ===\n";
    csvContent += "Hizmet Adi;Randevu Sayisi;Ciro (TL)\n";
    reportData.serviceStats.forEach(s => {
      csvContent += `"${s.serviceName}";${s.count};${s.revenue}\n`;
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `rezervioo_rapor_${startDate}_${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (error) {
    return (
      <div className="tab-pane active" id="reports">
        <div className="admin-header">
          <h2>İşletme Raporları</h2>
        </div>
        <div className="admin-content">
          <div className="alert-error">{error}</div>
          <button className="btn-primary mt-4" onClick={loadReports}>Tekrar Dene</button>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-pane active" id="reports">
      <div className="admin-header" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', padding: '1.5rem', marginBottom: '2rem' }}>
        <div>
          <h2>İşletme Raporları</h2>
          {/* <p className="section-subtitle">Tarih aralığına göre işletmenizin performansını analiz edin.</p> */}
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <CalendarDays size={18} color="#888" />
            <input
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ padding: '0.4rem', width: '130px' }}
            />
            <span style={{ color: '#888' }}>-</span>
            <input
              type="date"
              className="form-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '0.4rem', width: '130px' }}
            />
          </div>
          <button
            className="btn-primary"
            onClick={exportToCSV}
            disabled={!reportData || loading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 1rem' }}
          >
            <Download size={18} />
            CSV İndir
          </button>
        </div>
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="admin-empty">
            <p>Raporlar yükleniyor...</p>
          </div>
        ) : !reportData ? (
          <div className="admin-empty">
            <p>Rapor bulunamadı.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>

            {/* Ozet Kartlari */}
            <div className="summary-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
              <div className="summary-box">
                <h3 className="summary-title"><TrendingUp size={18} /> Toplam Ciro</h3>
                <div className="value" style={{ color: 'var(--gold-primary)', fontSize: '2rem' }}>
                  ₺{reportData.totalRevenue.toLocaleString('tr-TR')}
                </div>
              </div>
              <div className="summary-box">
                <h3 className="summary-title"><CalendarDays size={18} /> Toplam Randevu</h3>
                <div className="value" style={{ fontSize: '2rem' }}>
                  {reportData.totalAppointments}
                </div>
              </div>
              <div className="summary-box">
                <h3 className="summary-title"><Users size={18} /> Çalışan Sayısı</h3>
                <div className="value" style={{ fontSize: '2rem' }}>
                  {reportData.staffStats.length}
                </div>
              </div>
              <div className="summary-box">
                <h3 className="summary-title"><Scissors size={18} /> Tercih Edilen Hizmetler</h3>
                <div className="value" style={{ fontSize: '2rem' }}>
                  {reportData.serviceStats.length}
                </div>
              </div>
            </div>

            {/* Grafikler */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>

              {/* Personel Ciro Grafigi */}
              <div className="stat-card" style={{ padding: '1.5rem', background: '#1d1f24', borderRadius: '12px', border: '1px solid #333' }}>
                <h3 style={{ color: '#fff', marginBottom: '1.5rem', fontSize: '1.1rem' }}>Personel Ciro Dağılımı</h3>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData.staffStats} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="staffName" stroke="#888" />
                      <YAxis stroke="#888" />
                      <RechartsTooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #333', borderRadius: '8px' }} />
                      <Legend />
                      <Bar dataKey="revenue" name="Ciro (₺)" fill="var(--gold-primary)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Hizmet Tercih Grafigi */}
              <div className="stat-card" style={{ padding: '1.5rem', background: '#1d1f24', borderRadius: '12px', border: '1px solid #333' }}>
                <h3 style={{ color: '#fff', marginBottom: '1.5rem', fontSize: '1.1rem' }}>En Çok Tercih Edilen Hizmetler</h3>
                <div style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={reportData.serviceStats}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="serviceName"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {reportData.serviceStats.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip contentStyle={{ backgroundColor: '#111', border: '1px solid #333', borderRadius: '8px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

            </div>

            {/* Tablolar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '2rem' }}>
              <div className="admin-table-wrapper" style={{ marginTop: 0 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Personel</th>
                      <th>Randevu</th>
                      <th>Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.staffStats.map((staff, i) => (
                      <tr key={i}>
                        <td style={{ color: '#fff' }}>{staff.staffName}</td>
                        <td>{staff.count}</td>
                        <td style={{ color: 'var(--gold-primary)' }}>₺{staff.revenue.toLocaleString('tr-TR')}</td>
                      </tr>
                    ))}
                    {reportData.staffStats.length === 0 && (
                      <tr><td colSpan="3" style={{ textAlign: 'center' }}>Veri bulunamadı</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="admin-table-wrapper" style={{ marginTop: 0 }}>
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Hizmet</th>
                      <th>Randevu</th>
                      <th>Ciro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.serviceStats.map((srv, i) => (
                      <tr key={i}>
                        <td style={{ color: '#fff' }}>{srv.serviceName}</td>
                        <td>{srv.count}</td>
                        <td style={{ color: 'var(--gold-primary)' }}>₺{srv.revenue.toLocaleString('tr-TR')}</td>
                      </tr>
                    ))}
                    {reportData.serviceStats.length === 0 && (
                      <tr><td colSpan="3" style={{ textAlign: 'center' }}>Veri bulunamadı</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
